/**
 * Accessibilita' dei documenti: vocabolario, toni e rimedi.
 *
 * Vale qui la stessa avvertenza di `lib/documenti.ts`: questo modulo serve a
 * **guidare l'operatore**, non a proteggere il sistema. Il verdetto lo produce
 * la pipeline, con veraPDF quando c'e' e con l'analisi strutturale interna
 * altrimenti; qui si decide soltanto come mostrarlo e che cosa suggerire.
 *
 * Una proprieta' da non perdere, perche' e' il motivo per cui gli stati sono
 * cinque e non tre: `verificata` la puo' produrre **solo** il validatore
 * ufficiale. Cio' che supera la sola analisi interna e' `presunta`, e i due
 * verdetti non vanno fusi ne' in interfaccia ne' nei conteggi — il primo dice
 * «e' conforme», il secondo «dichiara cio' che serve, ma nessuno ha verificato».
 */

import type {
  CopiaCollegata,
  Documento,
  StatoAccessibilita,
  ValidatoreAccessibilita,
} from "../api/types";
import type { Tono } from "./toni";

/** Nome del controllo PDF/UA negli esiti, come lo scrive la pipeline. */
const CONTROLLO_PDF_UA = "pdf_ua";

/**
 * Tono del chip per ciascuno stato: giallo, arancio, rosso.
 *
 * Tre gradini, nell'ordine in cui chiedono di intervenire:
 *
 * * `presunta` e' **giallo**: il file dichiara cio' che serve, ma nessuno l'ha
 *   certificato, e il giudizio potrebbe ancora rivelarsi negativo;
 * * `non_garantita` e' **arancio**: mancano requisiti, il file va rifatto, ma
 *   resta pubblicato e raggiungibile;
 * * `non_verificata` e' **rosso**: il sistema non e' riuscito a leggere il
 *   file, e quindi non sa nemmeno che cosa manchi.
 *
 * Il rosso a `non_verificata` rovescia la scelta precedente, che lo voleva
 * grigio: un file che il sistema non riesce a leggere e' proprio quello su cui
 * nessun altro giudizio e' possibile, e non deve confondersi con «niente da
 * fare».
 */
export const TONO_ACCESSIBILITA: Record<StatoAccessibilita, Tono> = {
  verificata: "positivo",
  presunta: "attenzione",
  non_garantita: "grave",
  non_verificata: "critico",
  non_applicabile: "neutro",
};

/**
 * Etichetta breve, quella che sta dentro un chip in una cella di tabella.
 *
 * E' **l'unico vocabolario** del back-office: chip, contatori, filtri e legenda
 * dicono le stesse parole, e nessuna pagina ne scrive di proprie. Erano tre
 * nomi per lo stesso stato — «accessibile» nel chip, «Verificati accessibili»
 * nel contatore, «verificato accessibile» sul portale — e chi li incontrava in
 * punti diversi li prendeva per tre cose diverse. Le frasi del portale restano
 * a parte (`portale/modelli.py`), perche' il testo pubblico lo approva il
 * Comune.
 *
 * Due scelte di parola, entrambe per chi opera e non per chi sviluppa:
 *
 * * `presunta` e' «forse non accessibile», non «presunto accessibile» ne'
 *   «probabilmente accessibile»: «presunto» suonava come un giudizio
 *   giuridico, e «probabilmente accessibile» rassicurava piu' di quanto lo
 *   stato consenta — dice solo che il controllo semplificato e' passato e
 *   quello ufficiale manca;
 * * `non_verificata` e' «controllo non riuscito» e non «da verificare»: la
 *   seconda sembrava un compito per l'operatore, che invece non ha nulla da
 *   verificare a mano — puo' solo far ripetere il controllo al sistema.
 */
export const ETICHETTA_ACCESSIBILITA: Record<StatoAccessibilita, string> = {
  verificata: "accessibile",
  presunta: "forse non accessibile",
  non_garantita: "da correggere",
  non_verificata: "controllo non riuscito",
  non_applicabile: "non applicabile",
};

/** L'etichetta con l'iniziale maiuscola, per titoli e contatori. */
export function etichettaTitolo(stato: StatoAccessibilita): string {
  const etichetta = ETICHETTA_ACCESSIBILITA[stato];
  return etichetta.charAt(0).toUpperCase() + etichetta.slice(1);
}

/**
 * Che cosa ha prodotto una riverifica, detto anche quando non ha cambiato nulla.
 *
 * «Riprova» su un controllo che fallisce ancora lasciava il riquadro identico:
 * chi aveva premuto non sapeva se il controllo fosse ripartito. E la riverifica
 * riguarda solo il documento su cui si preme — le copie collegate hanno lo
 * stesso file ma conservano il giudizio che avevano —, quindi lo si dice
 * quando ce ne sono.
 */
export function esitoRiverifica(
  titolo: string,
  prima: StatoAccessibilita,
  dopo: StatoAccessibilita,
  copie: readonly Pick<CopiaCollegata, "denominazione">[] = [],
): string {
  const giudizio =
    prima === dopo
      ? `«${titolo}»: controllo ripetuto, esito invariato (${ETICHETTA_ACCESSIBILITA[dopo]}).`
      : `«${titolo}»: da «${ETICHETTA_ACCESSIBILITA[prima]}» a «${ETICHETTA_ACCESSIBILITA[dopo]}».`;
  if (copie.length === 0) return giudizio;
  const dove = copie.map((copia) => copia.denominazione).join(", ");
  return `${giudizio} Le copie collegate (${dove}) hanno lo stesso file ma conservano il giudizio di prima: si riverificano una per una, da «Accessibilita' dei documenti».`;
}

/**
 * Da dove viene il giudizio e come cambia: la frase che precede la legenda.
 *
 * E' la risposta alla domanda «come si passa da uno stato all'altro»: non lo
 * decide chi carica, e i gesti che lo cambiano sono due.
 */
export const COME_NASCE_IL_GIUDIZIO =
  "Il giudizio e' attribuito dal sistema a ogni caricamento. Si aggiorna con " +
  "«Riverifica» o sostituendo il file con «Sostituisci».";

/** Che cosa significa lo stato, in parole di chi carica i documenti. */
export const DESCRIZIONE_ACCESSIBILITA: Record<StatoAccessibilita, string> = {
  verificata: "Conforme allo standard PDF/UA secondo il validatore ufficiale veraPDF.",
  presunta:
    "Tag, titolo e lingua presenti secondo il controllo semplificato interno. " +
    "Manca la verifica ufficiale.",
  non_garantita:
    "Mancano requisiti di accessibilita' (tag, titolo o lingua) o sono presenti " +
    "pagine da scansione. Il documento resta pubblicato, ma potrebbe non essere " +
    "fruibile con le tecnologie assistive.",
  non_verificata:
    "Analisi non riuscita: il file potrebbe essere protetto da password, " +
    "danneggiato o troppo grande.",
  non_applicabile: "Formato diverso dal PDF: il controllo di accessibilita' non si applica.",
};

/**
 * Che cosa fare, o `null` dove non c'e' nulla da fare.
 *
 * E' la riga che mancava: lo stato diceva come stava il file, e chi lo leggeva
 * restava a chiedersi se toccasse a lui e con quale gesto. I gesti possibili
 * sono due soli — far ripetere il controllo, o caricare un file corretto al
 * posto di quello — e ogni rimedio li nomina con il testo dei pulsanti.
 */
export const COSA_FARE: Record<StatoAccessibilita, string | null> = {
  verificata: null,
  presunta: "Eseguire «Riverifica» quando il validatore ufficiale e' disponibile.",
  non_garantita:
    "Riesportare il PDF dal programma di origine con l'opzione per " +
    "l'accessibilita' attiva e caricarlo con «Sostituisci». Per le pagine da " +
    "scansione occorre il documento originale.",
  non_verificata:
    "Premere «Riprova». Se l'esito non cambia, riesportare il file e caricarlo " +
    "con «Sostituisci».",
  non_applicabile: null,
};

/** Nome leggibile dei controlli che la pipeline registra negli esiti. */
export const ETICHETTA_CONTROLLO: Readonly<Record<string, string>> = {
  pdf_da_scansione: "Pagine da scansione",
  pdf_ua: "Accessibilita' del PDF (PDF/UA)",
};

/**
 * Il nome di un controllo come si mostra; quello grezzo se non e' noto.
 *
 * Un controllo che la pipeline aggiungesse domani non deve sparire dal
 * dettaglio solo perche' qui nessuno gli ha ancora dato un nome.
 */
export function etichettaControllo(nome: string): string {
  return ETICHETTA_CONTROLLO[nome] ?? nome;
}

/**
 * Stati spiegati nella legenda, dal migliore al peggiore.
 *
 * `non_applicabile` non c'e': il chip non lo mostra (un JSON non ha un
 * controllo da riferire), e spiegare un'etichetta che non compare mai
 * aggiungerebbe una riga da leggere senza nulla da riconoscere.
 */
export const STATI_IN_LEGENDA: readonly StatoAccessibilita[] = [
  "verificata",
  "presunta",
  "non_garantita",
  "non_verificata",
];

/** Stati su cui ha senso filtrare per correggere, nell'ordine dell'urgenza. */
export const STATI_DA_CORREGGERE: readonly StatoAccessibilita[] = [
  "non_garantita",
  "non_verificata",
];

/** Stato di un documento, con il ripiego per gli esiti scritti prima. */
export function statoDi(documento: Documento): StatoAccessibilita {
  return documento.accessibilita ?? "non_verificata";
}

/**
 * Chi ha espresso il verdetto, in una riga leggibile, o `null` se non si sa.
 *
 * Preferisce la `fonte` registrata nel controllo — che porta la versione del
 * validatore e il profilo — e ripiega sull'enumerazione. Serve a rispondere
 * alla domanda che chi legge si pone subito dopo «non accessibile», cioe'
 * «secondo chi».
 */
export function fonteDi(documento: Documento): string | null {
  const controllo = (documento.esiti_validazione?.controlli ?? []).find(
    (voce) => voce.nome === CONTROLLO_PDF_UA,
  );
  if (controllo?.fonte) return controllo.fonte;
  return etichettaValidatore(documento.accessibilita_validatore ?? null);
}

function etichettaValidatore(
  validatore: ValidatoreAccessibilita | null,
): string | null {
  if (validatore === "verapdf") return "validatore ufficiale veraPDF";
  if (validatore === "euristica") return "analisi strutturale interna";
  return null;
}

/** «veraPDF 1.30.2 · profilo PDF/UA-1»: la versione e' il secondo elemento. */
const FONTE_VERAPDF = /^veraPDF\s+(\d+(?:\.\d+)*)/;

/** «euristica interna (veraPDF assente: …)»: il motivo sta fra parentesi. */
const FONTE_EURISTICA = /^euristica interna(?:\s*\((.*)\))?\s*$/s;

/**
 * Chi ha giudicato, in due righe: una per chi opera e una per chi assiste.
 *
 * `testo` e' cio' che si legge nella colonna: «Verifica ufficiale (veraPDF
 * 1.30.2)» oppure «Controllo semplificato». `tecnica` e' la fonte grezza o il
 * motivo per cui il validatore ufficiale non ha parlato — «veraPDF assente:
 * java non trovato» — che a un operatore non dice nulla e a chi gli fa
 * assistenza dice tutto; si mostra in piccolo, sotto.
 *
 * `fonteDi` resta com'e': e' la stessa informazione senza la traduzione, e la
 * usano le verifiche per accertare che la fonte non venga mai inventata.
 */
export function fonteLeggibile(documento: Documento): FonteLeggibile | null {
  const grezza = fonteDi(documento);
  return grezza === null ? null : traduciFonte(grezza);
}

/** Chi ha giudicato: la riga per chi opera e quella per chi assiste. */
export interface FonteLeggibile {
  testo: string;
  tecnica: string | null;
}

/**
 * La traduzione di una fonte grezza, come la scrive la pipeline.
 *
 * Esportata a parte perche' le verifiche la collaudano sulle fonti grezze,
 * senza costruire un documento intorno a ciascuna.
 */
export function traduciFonte(grezza: string): FonteLeggibile {
  const veraPdf = FONTE_VERAPDF.exec(grezza);
  if (veraPdf) return { testo: `Verifica ufficiale (veraPDF ${veraPdf[1]})`, tecnica: grezza };
  const euristica = FONTE_EURISTICA.exec(grezza);
  if (euristica) return { testo: "Controllo semplificato", tecnica: euristica[1] ?? null };
  if (grezza === etichettaValidatore("verapdf")) {
    return { testo: "Verifica ufficiale (veraPDF)", tecnica: null };
  }
  if (grezza === etichettaValidatore("euristica")) {
    return { testo: "Controllo semplificato", tecnica: null };
  }
  // Una fonte che non si riconosce si mostra com'e': tradurla a caso direbbe
  // chi ha giudicato senza saperlo.
  return { testo: grezza, tecnica: null };
}

/**
 * Ordine delle voci nel riepilogo: prima cio' su cui si deve intervenire, dal
 * rosso al verde. Il primo stato presente da' anche il tono del riepilogo.
 */
const ORDINE_RIEPILOGO: readonly StatoAccessibilita[] = [
  "non_verificata",
  "non_garantita",
  "presunta",
  "verificata",
];

/**
 * Riassunto di un gruppo di documenti, per la riga di una pubblicazione.
 *
 * Prende il posto di «Validata / Con avvisi / Validazione in corso», che
 * parlava un secondo vocabolario accanto a quello dell'accessibilita': lo
 * stesso documento poteva essere «con avvisi» in una colonna e «da correggere»
 * in un'altra, e nessuno capiva che erano la stessa cosa. Restano due sole
 * informazioni, nelle stesse parole del resto del back-office: se qualcosa
 * **blocca** la pubblicazione (le pagine da scansione, l'unico caso normativo)
 * e quanti file stanno in ciascuno stato di accessibilita'.
 */
export function riepilogoAccessibilita(
  documenti: readonly Documento[],
): { tono: Tono; testo: string } | null {
  if (documenti.length === 0) return null;
  const bloccato = documenti.some((documento) =>
    (documento.esiti_validazione?.controlli ?? []).some(
      (controllo) => controllo.esito === "fallito" && controllo.bloccante,
    ),
  );
  if (bloccato) return { tono: "critico", testo: "non pubblicabile: pagine da scansione" };
  const conteggi = conteggiPerStato(documenti);
  const presenti = ORDINE_RIEPILOGO.filter((stato) => (conteggi[stato] ?? 0) > 0);
  if (presenti.length === 0) return null;
  const voci = presenti.map((stato) => `${conteggi[stato]} ${ETICHETTA_ACCESSIBILITA[stato]}`);
  return { tono: TONO_ACCESSIBILITA[presenti[0]], testo: voci.join(" · ") };
}


/**
 * Il riassunto che apre l'elenco delle mancanze: «3 errori rilevati».
 *
 * In colonna l'elenco intero allungava ogni riga fino a far scorrere la tabella
 * per pagine: si vede il numero, e le voci si aprono a richiesta.
 */
export function testoErroriRilevati(quanti: number): string {
  return quanti === 1 ? "1 errore rilevato" : `${quanti} errori rilevati`;
}

/**
 * Per che cosa il documento non va, una voce breve per requisito mancante.
 *
 * Legge `mancanze`, che il server compila come elenco di cio' che manca —
 * «Lingua del documento non dichiarata» — senza il rimedio. E' cio' che serve
 * in una colonna: chi scorre l'elenco vuole sapere che cosa non e' soddisfatto,
 * non leggere una procedura per riga. Il rimedio, uno per stato, lo da'
 * `COSA_FARE`.
 *
 * Il client **non inventa** voci. Sugli esiti scritti prima che `mancanze`
 * esistesse ricava le voci da cio' che l'esito porta gia':
 *
 * * dalle righe di veraPDF, una per regola violata: la parte che dice che cosa
 *   manca, prima dei due punti e senza la clausola. Il messaggio di quegli
 *   esiti e' un conteggio («ha rilevato 2 requisiti»), e ripiegare su di lui
 *   perdeva proprio l'informazione per cui la colonna esiste;
 * * altrimenti dal messaggio del controllo, ridotto alla prima frase: se quella
 *   elenca i requisiti («…non soddisfatti: a; b»), le voci sono quelle.
 */
export function mancanzeDi(documento: Documento): string[] {
  const controlli = documento.esiti_validazione?.controlli ?? [];
  const voci: string[] = [];
  for (const controllo of controlli) {
    if (controllo.esito === "superato") continue;
    const mancanze = controllo.mancanze ?? [];
    if (mancanze.length > 0) {
      voci.push(...mancanze);
      continue;
    }
    const daRegole = vociDalleRegole(controllo.dettagli ?? []);
    voci.push(...(daRegole.length > 0 ? daRegole : vociDalMessaggio(controllo.messaggio)));
  }
  // Piu' controlli possono dire la stessa cosa: in colonna basta una volta.
  return [...new Set(voci)];
}

/** La clausola in coda a una riga di veraPDF: «… (ISO 14289-1, clausola 7.21)». */
const CODA_CLAUSOLA = /\s*\(ISO 14289-1, clausola [^)]*\)\s*$/;

/**
 * Il ripiego di {@link mancanzeDi} per gli esiti di veraPDF senza `mancanze`.
 *
 * Ogni riga e' «che cosa manca: come si rimedia (ISO 14289-1, clausola X)»; se
 * ne tiene la prima parte. Le righe senza la clausola in coda sono rimedi
 * dell'analisi interna, e si lasciano al ripiego sul messaggio.
 */
function vociDalleRegole(dettagli: readonly string[]): string[] {
  return dettagli
    .filter((riga) => CODA_CLAUSOLA.test(riga))
    .map((riga) => riga.replace(CODA_CLAUSOLA, "").split(": ")[0].replace(/\.$/, "").trim())
    .filter(Boolean);
}

/** Il ripiego di {@link mancanzeDi} sul messaggio del controllo. */
function vociDalMessaggio(messaggio: string | undefined): string[] {
  const testo = (messaggio ?? "").trim();
  if (!testo) return [];
  // La prima frase: il resto del messaggio spiega le conseguenze, non che cosa
  // manca. Si taglia su un punto seguito da una maiuscola, cosi' «d.lgs.» o un
  // numero decimale non spezzano la frase.
  const prima = testo.split(/\.\s+(?=[A-ZÀ-Ý«])/)[0].replace(/\.$/, "").trim();
  const elenco = /non soddisfatti:\s*(.+)$/i.exec(prima);
  if (elenco) {
    const voci = elenco[1]
      .split(";")
      .map((voce) => voce.trim())
      .filter(Boolean)
      .map((voce) => voce.charAt(0).toUpperCase() + voce.slice(1));
    if (voci.length > 0) return voci;
  }
  return [prima];
}

/**
 * L'intervento da mostrare fuori dal menu degli errori, o `null`.
 *
 * Di norma l'intervento sta nel menu «N errori rilevati», insieme agli errori.
 * Un controllo non riuscito pero' non lascia errori da contare — l'analisi non
 * si e' conclusa e l'API non registra alcun controllo —, e senza questa riga
 * dopo un caricamento restavano il chip e «Riprova», senza dire che cosa fare
 * se l'esito non cambia. «Forse non accessibile» invece tace: con veraPDF
 * assente lo e' ogni PDF, e la stessa riga sotto ciascun documento sarebbe
 * rumore.
 */
export function interventoSenzaErrori(documento: Documento): string | null {
  const stato = statoDi(documento);
  if (stato !== "non_verificata" || mancanzeDi(documento).length > 0) return null;
  return COSA_FARE[stato];
}

/** Vero se il documento e' fra quelli su cui il redattore deve intervenire. */
export function daCorreggere(documento: Documento): boolean {
  return STATI_DA_CORREGGERE.includes(statoDi(documento));
}

/**
 * Conteggio per stato a partire da un elenco di documenti.
 *
 * Da usare **solo** quando l'elenco e' completo. Quando la risposta dell'API
 * porta i propri `conteggi`, sono quelli a valere: li calcola sull'intero
 * filtro e non sulla pagina, e su un archivio paginato le due cifre divergono.
 */
export function conteggiPerStato(
  documenti: readonly Documento[],
): Record<string, number> {
  const conteggi: Record<string, number> = {};
  for (const documento of documenti) {
    const stato = statoDi(documento);
    conteggi[stato] = (conteggi[stato] ?? 0) + 1;
  }
  return conteggi;
}

/** Documenti raggruppati per obbligo, nell'ordine in cui sono arrivati. */
export function raggruppaPerNodo<
  T extends { nodo?: { id_nodo: string; denominazione: string } | null },
>(documenti: readonly T[]): { chiave: string; denominazione: string; documenti: T[] }[] {
  const gruppi = new Map<string, { chiave: string; denominazione: string; documenti: T[] }>();
  for (const documento of documenti) {
    const chiave = documento.nodo?.id_nodo ?? "";
    const gruppo = gruppi.get(chiave) ?? {
      chiave,
      denominazione: documento.nodo?.denominazione ?? "Obbligo non identificato",
      documenti: [],
    };
    gruppo.documenti.push(documento);
    gruppi.set(chiave, gruppo);
  }
  return [...gruppi.values()];
}

/**
 * Vero se non c'e' nulla da chiedere all'API: il risultato e' vuoto per
 * costruzione, e la richiesta sarebbe sbagliata oltre che inutile.
 *
 * Due casi, e in entrambi il danno e' che l'API risponderebbe **qualcosa**.
 *
 * Il super admin e' cross-ente: senza un ente scelto non esiste un archivio su
 * cui cercare, e lasciar decidere al database quale servire e' peggio che non
 * servirne nessuno.
 *
 * Nessuno stato selezionato significa **nessun documento**, non «tutti». L'API
 * tratta un filtro vuoto come assenza di filtro — e' la convenzione giusta per
 * un parametro facoltativo — quindi spegnere tutte le caselle avrebbe mostrato
 * l'intero archivio, comprese le righe `non_applicabile` che questa pagina
 * dichiara di escludere per scelta e che chi deve correggere non ha motivo di
 * vedere. Lo stato vuoto della pagina promette il contrario, ed e' la promessa
 * giusta: la si mantiene qui, non sperando che l'API indovini.
 *
 * Sta in questo modulo e non nel componente perche' e' una regola, e le regole
 * di questo back-office si collaudano (`verifiche/`); i componenti no, per la
 * ragione dichiarata in `verifiche/esegui.mjs`.
 */
export function ricercaSenzaOggetto(opzioni: {
  stati: readonly StatoAccessibilita[];
  enteId: number | null;
  eSuperAdmin: boolean;
}): boolean {
  if (opzioni.eSuperAdmin && opzioni.enteId === null) return true;
  return opzioni.stati.length === 0;
}
